// We're getting a bunch of html from the server and shoving it into react here.

import * as React from 'react';
import {ApolloProvider} from 'react-apollo';
import ReactDOM from 'react-dom';
import {Helmet} from 'react-helmet';
import {Provider} from 'react-redux';
import {Prompt, Router} from 'react-router-dom';
// @ts-expect-error
import type {RouterHistory, Location} from 'react-router-dom';

import {ApolloProvider as ReactHooksApolloProvider} from '@apollo/react-hooks';

import {api, Api} from 'nutshell-core/api';

import {headTitle} from 'shells/document/head-title';

import {nutApolloClient} from '../../../apollo-client';
import {appHistory} from '../../../history';
import {store} from '../../../store';
import {getRouteChangeBlockingMessage} from '../../../util/get-route-change-blocking-message';

import {TimelineSendEmailButton} from '../../email/timeline-send-email-button';
import {FileAttachments} from '../../entity-details/files/file-attachments';
import {FileSection} from '../../entity-details/files/file-section';
import {SourcesSectionWrapper} from '../../entity-details/sources/sources-section-wrapper';
import {PageWrapper} from '../../layout/page-wrapper';
import {ActivitiesSection} from '../../peep/activities/activities-section';
import {EntityPageEmailSequencesSection} from '../../peep/email-marketing/email-sequences/entity-page-email-sequences-section';
import {ACTIVITIES_REACT_MOUNT_ID, TIMELINE_REACT_MOUNT_ID} from '../../peep/peep-page';
import {PeopleIqBadge} from '../../people-iq';
import {EntityPageTimeline} from '../../timeline/entity-page-timeline';

import {LeadLimitUpsellModal} from '../shared/components';

import {LeadDripSequenceProgressionSection} from './email-marketing/lead-drip-sequence-progression-section';
import {GetLead} from './get-lead';
import {LeadContents} from './lead-contents';
import {SidebarSalesDocumentsSection} from './sales-documents-section/sidebar-sales-documents-section';

import styles from './lead-page.module.css';

type Props = {
    leadId?: string;
    leadNum?: number;
    history: RouterHistory;
    location: Location;
};

type State = {
    sidebarMarkup: HTMLElement | null | undefined;
    leadEnclosure: HTMLElement | null | undefined;
    leadId: string | null | undefined;
    /** We get the leadNum either from the url, or from the lead itself */
    leadNum: string | null | undefined;
    leadName: string | null | undefined;
    didRenderLeadEnclosure: boolean;
};

const LEFT_COLUMN_SELECTOR = '#lead-enclosure';
const RIGHT_COLUMN_SELECTOR = '#lead-sidebar';
// Our API class defaults its baseUrl to be `/rest/`, so we
// need to construct a new one and override that here as we're
// not hitting the rest API for these billing calls.
const viewApi = new Api('/');
const headers = new Headers({
    'Content-Type': 'application/xhtml+xml',
    accept: '*/*',
    'X-Requested-With': 'XMLHttpRequest',
});

document.addEventListener('Lead initialized', () => {
    const sourcesSection = document.querySelector('.lead-sources-section');
    if (sourcesSection) {
        ReactDOM.render(
            <ApolloProvider client={nutApolloClient}>
                <ReactHooksApolloProvider client={nutApolloClient}>
                    <Provider store={store}>
                        <Router history={appHistory}>
                            {/* @ts-expect-error */}
                            <SourcesSectionWrapper model={window.currentLead} />
                        </Router>
                    </Provider>
                </ReactHooksApolloProvider>
            </ApolloProvider>,
            sourcesSection
        );
    }

    const quotesSection = document.querySelector('.lead-quotes-section');
    if (quotesSection) {
        ReactDOM.render(
            <ApolloProvider client={nutApolloClient}>
                <ReactHooksApolloProvider client={nutApolloClient}>
                    <Router history={appHistory}>
                        <SidebarSalesDocumentsSection />
                    </Router>
                </ReactHooksApolloProvider>
            </ApolloProvider>,
            quotesSection
        );
    }

    const fileSection = document.querySelector('.lead-file-section');
    if (fileSection) {
        // @ts-expect-error
        ReactDOM.render(<FileSection model={window.currentLead} />, fileSection);
    }

    const emailCreatorFileSection = document.querySelector('.creator-email-file-section');
    if (emailCreatorFileSection) {
        ReactDOM.render(<FileAttachments model={Timeline.creatorEmail} />, emailCreatorFileSection);
    }
});

document.addEventListener('Card enclosure reload', () => {
    const quotesSection = document.querySelector('.lead-quotes-section');
    if (quotesSection) {
        ReactDOM.render(
            <ApolloProvider client={nutApolloClient}>
                <ReactHooksApolloProvider client={nutApolloClient}>
                    <Router history={appHistory}>
                        <SidebarSalesDocumentsSection />
                    </Router>
                </ReactHooksApolloProvider>
            </ApolloProvider>,
            quotesSection
        );
    }
});

export class LeadPage extends React.Component<Props, State> {
    scrollContainerRef: HTMLDivElement | null | undefined;

    constructor(props: Props) {
        super(props);
        this.state = {
            sidebarMarkup: null,
            leadEnclosure: null,
            leadId: typeof props.leadId === 'string' ? props.leadId : null,
            leadNum: typeof props.leadNum === 'string' ? props.leadNum : null,
            leadName: null,
            didRenderLeadEnclosure: false,
        };
    }

    componentDidMount() {
        this.getLeadMarkup(this.props.leadId, this.props.leadNum);

        $('body').on(Nut.events.global.accountContactCardsWereReloaded, this.loadPeopleIqBadge);
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        if (nextProps.leadId !== this.props.leadId || nextProps.leadNum !== this.props.leadNum) {
            this.getLeadMarkup(nextProps.leadId, nextProps.leadNum);
        }
    }

    componentDidUpdate(prevProps: Props, prevState: State) {
        const {leadId, leadName} = this.state;

        // Change the URL to the lead/number, for consistency.
        if (this.props.location.pathname.includes('/lead/id') && this.state.leadNum) {
            this.props.history.replace(`/lead/${this.state.leadNum}`);
        }
        if (leadId && prevState.leadId !== leadId) {
            api.post(`views/${leadId}`, {});
        }
        if (
            prevState.leadEnclosure !== this.state.leadEnclosure ||
            prevState.sidebarMarkup !== this.state.sidebarMarkup ||
            prevState.didRenderLeadEnclosure !== this.state.didRenderLeadEnclosure ||
            prevState.leadId !== leadId ||
            prevState.leadName !== leadName
        ) {
            if (leadId) {
                // Put the Lead model on `window.currentLead` for legacy code
                Nut.modules.Lead.initializeOldBackboneModelForLeadId(leadId);
            }
            // Once we have the phtml, we can do things that needs the markup on the page
            if (
                this.state.leadEnclosure &&
                this.state.sidebarMarkup &&
                // This is a mess, but we need to wait for the lead enclosure div to
                // actually be rendered before we can call this. Otherwise a race
                // condition could cause the page to not render correctly.
                this.state.didRenderLeadEnclosure &&
                // didRenderLeadEnclosure is only ever called after the lead is fetched,
                // so this will always be true in this case.
                leadId
            ) {
                Nut.preparePage();
                Nut.updateAppearances();

                const timelineCreatorEmailButton = document.querySelector(
                    '#timeline-creator-emails-button-react-mount'
                );
                if (timelineCreatorEmailButton) {
                    const mount = document.createElement('div');
                    timelineCreatorEmailButton.appendChild(mount);
                    ReactDOM.render(
                        <Provider store={store}>
                            <TimelineSendEmailButton
                                entityId={leadId}
                                buttonNode={timelineCreatorEmailButton}
                            />
                        </Provider>,
                        mount
                    );
                }

                const dripSequenceProgressionMount = document.querySelector(
                    '#drip-sequence-progression-react-mount'
                );
                if (dripSequenceProgressionMount) {
                    ReactDOM.render(
                        <ApolloProvider client={nutApolloClient}>
                            <ReactHooksApolloProvider client={nutApolloClient}>
                                <Router history={appHistory}>
                                    <LeadDripSequenceProgressionSection leadId={leadId} />
                                </Router>
                            </ReactHooksApolloProvider>
                        </ApolloProvider>,
                        dripSequenceProgressionMount
                    );
                }

                const personalEmailSequencesMount = document.querySelector(
                    '#personal-email-sequences-react-mount'
                );
                if (personalEmailSequencesMount) {
                    ReactDOM.render(
                        <ApolloProvider client={nutApolloClient}>
                            <ReactHooksApolloProvider client={nutApolloClient}>
                                <Router history={appHistory}>
                                    <EntityPageEmailSequencesSection
                                        entityId={leadId}
                                        entityType='lead'
                                    />
                                </Router>
                            </ReactHooksApolloProvider>
                        </ApolloProvider>,
                        personalEmailSequencesMount
                    );
                }

                const timelineReactMount = document.querySelector(`#${TIMELINE_REACT_MOUNT_ID}`);
                if (timelineReactMount) {
                    ReactDOM.render(
                        <Provider store={store}>
                            <ApolloProvider client={nutApolloClient}>
                                <ReactHooksApolloProvider client={nutApolloClient}>
                                    <Router history={appHistory}>
                                        <EntityPageTimeline
                                            entityId={leadId}
                                            entityName='Leads'
                                            entityPageName={leadName}
                                            scrollContainerRef={this.scrollContainerRef}
                                        />
                                    </Router>
                                </ReactHooksApolloProvider>
                            </ApolloProvider>
                        </Provider>,
                        timelineReactMount
                    );
                }

                const activitiesReactMount = document.querySelector(
                    `#${ACTIVITIES_REACT_MOUNT_ID}`
                );
                if (activitiesReactMount) {
                    ReactDOM.render(
                        <Provider store={store}>
                            <ApolloProvider client={nutApolloClient}>
                                <ReactHooksApolloProvider client={nutApolloClient}>
                                    <Router history={appHistory}>
                                        <ActivitiesSection entityId={leadId} />
                                    </Router>
                                </ReactHooksApolloProvider>
                            </ApolloProvider>
                        </Provider>,
                        activitiesReactMount
                    );
                }

                this.loadPeopleIqBadge();
            }
        }
    }

    componentWillUnmount(): void {
        Popover.close();
        Lead.unloadLead();
        $('body').off(Nut.events.global.accountContactCardsWereReloaded, this.loadPeopleIqBadge);
    }

    render() {
        return (
            <PageWrapper>
                <GetLead
                    leadId={this.props.leadId}
                    leadNum={this.props.leadNum}
                    onCompleted={(data) => {
                        const lead = data && data.lead;
                        if (lead) {
                            if (lead.id && (!this.state.leadId || this.state.leadId !== lead.id)) {
                                this.setState({leadId: lead.id});
                                this.setState({leadName: lead.name});
                            }
                            // Set the leadNum if necessary, so we can change the url if we need to
                            if (
                                lead.number &&
                                // @ts-expect-error
                                (!this.state.leadNum || this.state.leadNum !== Number(lead.number))
                            ) {
                                this.setState({leadNum: String(lead.number)});
                                this.setState({leadName: lead.name});
                            }
                        }
                    }}
                >
                    {({lead, isLoading, errorMessage}) => {
                        if (
                            errorMessage === 'Lead limit exceeded' // Beware of changing- we string match an error from the back-end here
                        ) {
                            return <LeadLimitUpsellModal history={this.props.history} />;
                        }

                        return (
                            <div
                                ref={(node) => {
                                    this.scrollContainerRef = node;
                                }}
                                className={styles.container}
                            >
                                <Prompt
                                    // As documented here: https://reactrouter.com/core/api/Prompt, you can pass a function as the message
                                    // that returns a string to show a prompt to the user or true to allow the transition.
                                    // Here we want to warn users if they try to leave the page before saving their timeline input.
                                    message={(location, action) => {
                                        // This check is for flow - action should always exist
                                        if (action) {
                                            const blockingMessage =
                                                getRouteChangeBlockingMessage(action);
                                            if (blockingMessage) {
                                                return blockingMessage;
                                            }

                                            return true;
                                        }

                                        return true;
                                    }}
                                />
                                <Helmet>
                                    <title>{headTitle('Lead')}</title>
                                </Helmet>
                                <LeadContents
                                    leadEnclosure={this.state.leadEnclosure}
                                    sidebarMarkup={this.state.sidebarMarkup}
                                    lead={lead}
                                    onRenderLeadEnclosure={() => {
                                        // We need to know when the lead enclosure has rendered,
                                        // because otherwise we can hit a race condition where
                                        // we don't set up the modules correctly.
                                        if (!this.state.didRenderLeadEnclosure) {
                                            this.setState({didRenderLeadEnclosure: true});
                                        }
                                    }}
                                    isLoadingLead={isLoading}
                                    errorMessage={errorMessage}
                                    history={this.props.history}
                                />
                                {/* Required for dragging file uploads */}
                                <div className='drag-drop-overlay'>
                                    <div className='drag-drop-target-container' />
                                </div>
                            </div>
                        );
                    }}
                </GetLead>
            </PageWrapper>
        );
    }

    getLeadMarkup = (leadId?: string, leadNum?: number) => {
        let url;
        if (leadId) {
            url = `/lead/view.webchunk?id=${leadId}`;
        } else if (leadNum) {
            url = `/lead/view.webchunk?number=${leadNum}`;
        }

        if (url) {
            this.setState({
                didRenderLeadEnclosure: false,
                sidebarMarkup: null,
                leadEnclosure: null,
            });

            viewApi
                // @ts-expect-error
                .get(url, undefined, headers)
                .then((res) => {
                    // @ts-expect-error
                    if (res && res.redirected) {
                        // @ts-expect-error
                        window.location.href = res.url;
                    }

                    return res;
                })
                // @ts-expect-error
                .then((res) => res.text())
                .then((body) => {
                    const parser = new DOMParser();

                    // Parse the text
                    const doc = parser.parseFromString(body, 'text/html');

                    const sidebarMarkup = doc.querySelector(RIGHT_COLUMN_SELECTOR);
                    const leadEnclosure = doc.querySelector(LEFT_COLUMN_SELECTOR);
                    // @ts-expect-error
                    this.setState({sidebarMarkup, leadEnclosure});
                });
            // TODO: handle errors
        }
        // TODO: else redirect somewhere?
    };

    loadPeopleIqBadge = () => {
        const peopleIqReactMount = document.querySelector('#people-iq-account-card-react-mount');
        if (peopleIqReactMount) {
            // @ts-expect-error
            const leadId = `${peopleIqReactMount.dataset.leadId}-leads`;
            // @ts-expect-error
            const accountId = `${peopleIqReactMount.dataset.accountId}-accounts`;
            // @ts-expect-error
            const accountName = peopleIqReactMount.dataset.accountName;

            ReactDOM.render(
                <Provider store={store}>
                    <ApolloProvider client={nutApolloClient}>
                        <ReactHooksApolloProvider client={nutApolloClient}>
                            <Router history={appHistory}>
                                <PeopleIqBadge
                                    leadId={leadId}
                                    accountId={accountId}
                                    accountName={accountName}
                                />
                            </Router>
                        </ReactHooksApolloProvider>
                    </ApolloProvider>
                </Provider>,
                peopleIqReactMount
            );
        }
    };
}
